import React, { useState, useMemo, useEffect } from "react";
import { HiOutlineSearch, HiOutlineDotsVertical } from "react-icons/hi";
import { Link, useNavigate, useParams } from "react-router-dom";
import OptionAction from "./OptionAction";
import SendReminderModal from "../../Sales/invoice/SendReminderModal";
import { showToast } from "../../../utils/toastUtils";
import { Spinner } from "flowbite-react";
import { Tab } from "../../common";
import { ManualRecordPayment, UploadTransactionEvidence } from "../../payments";
import { getInvoice } from "../../../backend-services";
import RecordPaymentSuccess from "../../payments/RecordPayment/SuccessModal";
import { downloadReceipt } from "../../../backend-services/receipt";
import ThermalPrinter from "../../common/printer";
import {
  invoiceToReceiptData,
  ReceiptData,
} from "../../../utils/EscPosEncoder";
import { useProjectStore, useUserStore } from "../../../state";
import { useCompanyStore } from "../../../state/useCompanyStore";
import { IProject } from "../../../@types";
import { useProjectHook } from "../../../hooks/project/useProject";
import ConfirmationModal from "./ConfirmationModal";
import { set } from "lodash";

export interface IProjectTableData {
  id: string;
  createdAt: Date;
  amount: number;
  customer: string;
  customerId: string;
  balanceDue?: string;
  projectId: string;
  invoiceNumber?: number;
  type: "Expenses" | "Invoices";
}

interface TransactionsTableProps {
  data: IProjectTableData[];
  projectData: IProject;
  // onActionClick?: (item: IProjectTableData) => void;
  onActionClick: (id: string) => void;
  isDataLoading?: boolean;
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  data,
  projectData,
  onActionClick,
  isDataLoading = true,
}) => {
  const { getProjectById } = useProjectStore();
  const userStore = useCompanyStore();
  const { user, roles } = useUserStore();
  const isRetail = userStore.isRetailBusiness();
  const [selectInvoiceData, setSelectInvoiceData] =
    useState<IProjectTableData>();
  const [selectExpenseData, setSelectExpenseData] =
    useState<IProjectTableData>();
  const [invoiceData, setInvoiceData] = useState<any>();
  const [searchTerm, setSearchTerm] = useState("");
  const [showReminder, setShowReminder] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [balanceDue, setBalanceDue] = useState("");
  const [showSendModal, setShowSendModal] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const [receipt, setReceipt] = useState<ReceiptData>();
  const [printCounter, setPrintCounter] = useState(0);
  const [showInvoiceRemove, setShowInvoiceRemove] = useState(false);
  const [showExpenseRemove, setShowExpenseRemove] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [isSpinnerVisible, setIsSpinnerVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSpinnerVisible(false); // Hide the spinner after 5 seconds
    }, 3000);

    return () => clearTimeout(timer); // Cleanup the timer on component unmount
  }, []);

  const { toggleProjectInvoice, toggleProjectExpense, isRemoving } =
    useProjectHook({
      projectId: projectData.id,
    });

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter((item) =>
      item.customer.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [data, searchTerm]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };

  const getTypeColor = (type: string) => {
    return type === "Invoices" ? "text-green-600" : "text-orange-600";
  };
  const handleSendReminder = (item: IProjectTableData) => {
    console.log("Payment reminder clicked");
    setShowReminder(true);
    setSelectInvoiceData(item);
  };

  const fetchInvoice = async () => {
    try {
      const data = await getInvoice(selectExpenseData?.id || "");
      setInvoiceData(data);
    } catch (error) {
      console.error("Failed to load invoice", "error");
    }
  };
  useEffect(() => {
    fetchInvoice();
  }, [selectExpenseData]);

  const handleRecordPayment = (item: IProjectTableData) => {
    console.log("Mark as Paid clicked");
    setShowRecordPayment(true);
  };
  const handleNewPayment = () => {
    setReloadSignal((prevSignal) => prevSignal + 1);
  };

  const handleNewUploadPayment = async (isUploadedPayment: boolean) => {
    if (isUploadedPayment && selectInvoiceData?.id) {
      setReloadSignal((prevSignal) => prevSignal + 1);
    }
  };
  const handleDownloadReceipt = async (id: string) => {
    try {
      setIsDownloading(true);
      const response = await downloadReceipt(id);
      const blob = await fetch(response.url).then((res) => res.blob());
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Invoice-${selectInvoiceData?.id}.pdf`;
      a.click();
    } catch (error) {
      if (error instanceof Error) {
        showToast(error.message, "error", 5000);
      } else {
        showToast("An unknown error occurred", "error"); // Fallback for non-Error objects
      }
      console.error(error);
    } finally {
      setIsDownloading(false);
      setSuccess(false);
    }
  };

  const triggerPrint = () => {
    setPrintCounter((prevCounter) => prevCounter + 1);
  };
  useEffect(() => {
    if (user && selectInvoiceData) {
      const receipt = invoiceToReceiptData(invoiceData, user, isRetail);
      setReceipt(receipt);
    }
  }, [user]);

  const handleInvoiceRemove = async (invoiceId: string) => {
    const res = await toggleProjectInvoice({ invoiceId: invoiceId });
    if (res) {
      setShowInvoiceRemove(false);
      getProjectById(projectData?.id);
      onActionClick(invoiceId);
    }
  };

  const handleExpenseRemove = async (expenseId: string) => {
    const res = await toggleProjectExpense({ expenseId: expenseId });
    if (res) {
      setShowExpenseRemove(false);
      getProjectById(projectData?.id);
      onActionClick(expenseId);
    }
  };

  useEffect(() => {
    if (isDataLoading && !loadingCheck) {
      setLoadingCheck(true);
      setIsLoading(true);
    } else if (!isDataLoading && loadingCheck) {
      setLoadingCheck(false);
      setIsLoading(false);
    }
  }, [isDataLoading]);

  return (
    <>
      <ThermalPrinter receipt={receipt} printCounter={printCounter} />
      <div className="bg-white sm:my-8 my-6 border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:p-5 p-3 pb-4">
          <h1 className="xl:text-[20px] md:text-[18px] text-[16px] font-semibold text-gray-900 mb-2 sm:mb-0">
            Transactions
          </h1>

          {/* Search Bar */}
          <div className="relative w-full lg:w-96 sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <HiOutlineSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 sm:py-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block">
          <div className="">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-y">
                <tr className="text-left xl:text-[15px] md:text-[14px] text-[13px] text-gray-800 tracking-wider">
                  <th className="sm:px-5 px-3 py-3 font-semibold">
                    Client/Title
                  </th>
                  <th className="sm:px-5 px-3 py-3 font-semibold">Type</th>
                  <th className="sm:px-5 px-3 py-3 font-semibold">Date</th>
                  <th className="sm:px-5 px-3 py-3 font-semibold">Amount</th>
                  <th className="sm:px-5 px-3 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData?.map((item) => (
                  <tr key={item.id} className="hover:bg-sky-50">
                    <td className="sm:px-5 px-3 py-4 whitespace-nowrap">
                      {item.type == "Invoices" ? (
                        <Link
                          className={`font-medium ${item.customerId ? "text-[#044E97] hover:text-blue-700 cursor-pointer" : "text-gray-500 cursor-default"}`}
                          to={
                            item.customerId &&
                            `/customers/${item.customerId}/view`
                          }
                        >
                          {`${item.customer.trim() !== "" ? item.customer : "NA"}`}
                        </Link>
                      ) : (
                        <Link
                          className={`font-medium text-[#044E97] hover:text-blue-700`}
                          to={`/${item.type === "Expenses" ? "expenses" : "invoices"}/${item.id}/view`}
                        >
                          {item.customer}
                        </Link>
                      )}
                    </td>
                    <td className="sm:px-5 px-3 py-4 whitespace-nowrap">
                      <Link
                        className={`font-medium ${getTypeColor(item.type)} hover:text-blue-700`}
                        to={`/${item.type === "Expenses" ? "expenses" : "invoices"}/${item.id}/view`}
                      >
                        {item.type}
                      </Link>
                    </td>
                    <td className="sm:px-5 px-3 py-4 whitespace-nowrap text-gray-900">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="sm:px-5 px-3 py-4 whitespace-nowrap text-gray-900 font-medium">
                      {formatAmount(item.amount)}
                    </td>
                    <td className="sm:px-5 px-3 py-4 whitespace-nowrap">
                      <OptionAction
                        actionButton={
                          <button
                            onClick={() => {
                              item.type === "Expenses"
                                ? setSelectExpenseData(item)
                                : setSelectInvoiceData(item);
                              setBalanceDue(item.balanceDue || "");
                            }}
                            className="text-gray-400 hover:text-gray-600 active:text-[#044E97] ml-[7px] p-[6px] border hover:border-gray-400 active:border-[#044E97] rounded-md"
                          >
                            <HiOutlineDotsVertical className="h-5 w-5" />
                          </button>
                        }
                        options={
                          item.type === "Expenses"
                            ? [
                                {
                                  value: "remove",
                                  label: "Remove expense",
                                  onClick: () => setShowExpenseRemove(true),
                                },
                              ]
                            : [
                                {
                                  value: "paymentReminder",
                                  label: "Payment reminder",
                                  onClick: () => {
                                    handleSendReminder(item);
                                  },
                                },
                                {
                                  value: "markAsPaid",
                                  label: "Add payment to invoice",
                                  onClick: () => handleRecordPayment(item),
                                },
                                {
                                  value: "remove",
                                  label: "Remove invoice",
                                  onClick: () => setShowInvoiceRemove(true),
                                },
                              ]
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden border-t">
          {filteredData.map((item) => (
            <div
              key={item.id}
              className="bg-white border-b border-gray-200 sm:px-5 px-3 py-4 last:border-b-0"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-[#044E97] xl:text-[18px] md:text-[17px] text-[16px]">
                      {`${item.customer.trim() !== "" ? item.customer : "NA"}`}
                    </h3>
                    <span className="xl:text-[18px] md:text-[17px] text-[16px] font-semibold text-gray-900">
                      {formatAmount(item.amount)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center gap-4 xl:text-[16px] md:text-[15px] text-[14px]">
                    <span className={`font-medium ${getTypeColor(item.type)}`}>
                      {item.type.slice(0, -1)}
                    </span>
                    <span className="text-gray-600">
                      {formatDate(item.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Fetching Loading State */}
        {isLoading && isSpinnerVisible && (
          <div className="text-center py-12">
            <Spinner size="lg" light={true} />
          </div>
        )}

        {/* Empty State */}
        {filteredData.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <p className="text-gray-800">
              {searchTerm
                ? "No transactions found matching your search."
                : "No transactions available."}
            </p>
          </div>
        )}
      </div>

      {showReminder && (
        <SendReminderModal
          setIsSending={setIsSending}
          invoiceId={selectInvoiceData?.id}
          //@ts-ignore
          customer={selectInvoiceData?.customer}
          setShowSendModal={setShowReminder}
        />
      )}

      {showExpenseRemove && (
        <div className="w-full">
          <ConfirmationModal
            isOpen={showExpenseRemove}
            onClose={() => setShowExpenseRemove(false)}
            onConfirm={() => handleExpenseRemove(selectExpenseData?.id || "")}
            title="Remove Expense"
            message={`Are you sure you want to remove this expense from ${projectData.name} project?`}
            confirmBtnText={isRemoving ? "Removing..." : "Yes, Remove"}
            isProcessing={isRemoving}
          />
        </div>
      )}

      {showInvoiceRemove && (
        <div className="w-full">
          <ConfirmationModal
            isOpen={showInvoiceRemove}
            onClose={() => setShowInvoiceRemove(false)}
            onConfirm={() => handleInvoiceRemove(selectInvoiceData?.id || "")}
            title="Remove Invoice"
            message={`Are you sure you want to remove this invoice from ${projectData.name} project?`}
            confirmBtnText={isRemoving ? "Removing..." : "Yes, Remove"}
            isProcessing={isRemoving}
          />
        </div>
      )}

      {showRecordPayment && (
        <div className="">
          <Tab
            height="h-auto"
            title="Record Payment"
            onClose={() => setShowRecordPayment(false)}
            items={[
              {
                title: "Record Manually",
                component: (
                  <ManualRecordPayment
                    onRecordEntity={handleNewPayment}
                    entity={invoiceData}
                    invoiceId={selectInvoiceData?.id || ""}
                    balanceDue={balanceDue}
                    setSuccess={setSuccess}
                    closeModal={() => setShowRecordPayment(false)}
                  />
                ),
              },
              {
                title: "Upload Receipt",
                component: (
                  <UploadTransactionEvidence
                    entity={invoiceData}
                    onRecordEntity={handleNewUploadPayment}
                    paymentType="revenue"
                    closeModal={() => setShowRecordPayment(false)}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
      {success && (
        <RecordPaymentSuccess
          invoiceId={selectInvoiceData?.id}
          handleDownload={handleDownloadReceipt}
          setSuccess={setSuccess}
          setShowSendModal={setShowSendModal}
          triggerPrint={triggerPrint}
          isDownloading={isDownloading}
          showSendReciept={Number(selectInvoiceData?.balanceDue) == 0}
        />
      )}
    </>
  );
};

export default TransactionsTable;
